import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { IcoArrowLeft } from '@components/icons';
import { useAuth } from 'client/lib/auth';

type Props = {
  returnTarget: string | undefined;
};

/**
 * When a superadmin enters this editor via the email-schedule agenda's
 * compose flow, the picker mimics the course guide first and tags the URL
 * with `?return=email-schedule`. This banner gives them a one-click way to
 * unmimic and bounce back to the agenda — and surfaces who they're
 * impersonating so the identity swap isn't silent.
 */
export function ReturnToScheduleBanner({ returnTarget }: Props) {
  const [busy, setBusy] = useState(false);
  const auth = useAuth();
  const mimickedAs = auth.config?.coreUser ? auth.user : undefined;

  if (returnTarget !== 'email-schedule') {
    return undefined;
  }

  async function back() {
    if (busy) {
      return;
    }
    try {
      setBusy(true);
      await rpx.admin.unmimicUser();
      // Use a hard navigation so the new (unmimicked) session is picked up
      // for the agenda's data fetch.
      location.href = '/admin/email-schedule';
    } catch (err) {
      setBusy(false);
      showError(err);
    }
  }

  return (
    <div class="mb-4 flex items-center justify-between gap-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
      <span class="text-sm text-indigo-900">
        {mimickedAs
          ? `Composing as ${mimickedAs.name || mimickedAs.email}. We'll switch you back when you're done.`
          : 'You arrived here from the email schedule.'}
      </span>
      <button
        type="button"
        disabled={busy}
        onClick={back}
        class="inline-flex items-center gap-1 text-sm font-medium text-indigo-700 hover:text-indigo-900 disabled:opacity-50 shrink-0"
      >
        <IcoArrowLeft class="w-4 h-4" />
        Back to email schedule
      </button>
    </div>
  );
}
